import moment from 'moment';
import { getAgeFromDob } from '../../../utils/date';
import { adviserFactsheetOf } from '../../../utils/client-factsheet';
import { UNSET_PERSONALITY, UNSPECIFIED_GENDER, createDefaultClientFilters } from './clientFilters';
import type { ClientFilters } from './clientFilters';
import type { ClientFactsheet, QueueClient } from '../../../types/client';

const POWER_PREFIXES = new Set(['+', '>', '<', '#', '%', '@', '^', '!', '$', '&']);

const textOf = (value: unknown): string => (value === null || value === undefined ? '' : String(value));
const normalizedTextOf = (value: unknown): string => textOf(value).trim().toLocaleLowerCase();
const includesText = (value: unknown, query: string): boolean => normalizedTextOf(value).includes(query);

const numberOf = (value: unknown): number | null => {
  const parsed = Number.parseFloat(textOf(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const searchableValuesOf = (client: QueueClient, factsheet?: ClientFactsheet): unknown[] => [
  factsheet?.name,
  factsheet?.nickname,
  factsheet?.workLocation,
  factsheet?.phoneNumber,
  factsheet?.homeLocation,
  factsheet?.importance,
  factsheet?.personality,
  client.name,
  client.nickname,
  client.email,
  client.phoneNumber,
  client.createDate,
  ...(factsheet?.tags || []).map(tag => tag.text)
];

const matchesPowerToken = (
  client: QueueClient,
  factsheet: ClientFactsheet | undefined,
  prefix: string,
  rawValue: string
): boolean => {
  if (!rawValue) return true;
  const value = rawValue.trim();
  const query = value.toLocaleLowerCase();

  switch (prefix) {
    case '+':
      return Boolean(factsheet && includesText(factsheet.phoneNumber, query));
    case '>': {
      const target = numberOf(value);
      const income = numberOf(factsheet?.monthlyIncome);
      return target !== null && income !== null && income >= target;
    }
    case '<': {
      const target = numberOf(value);
      const expense = numberOf(factsheet?.monthlyExpense);
      return target !== null && expense !== null && expense <= target;
    }
    case '#':
      return Boolean(factsheet?.tags?.some(tag => includesText(tag.text, query)));
    case '%':
      return Boolean(factsheet?.families?.some(family => includesText(family, query)));
    case '@': {
      const match = value.match(/^(\d+)-(\d+)$/);
      if (!match || !factsheet) return false;
      const minimum = Number.parseInt(match[1], 10);
      const maximum = Number.parseInt(match[2], 10);
      const age = getAgeFromDob(factsheet.dob, factsheet.age);
      return Number.isFinite(age) && age >= minimum && age <= maximum;
    }
    case '^': {
      const month = Number.parseInt(value, 10);
      if (!factsheet?.dob || month < 1 || month > 12) return false;
      const dob = moment.parseZone(factsheet.dob);
      return dob.isValid() && dob.month() === month - 1;
    }
    case '!':
      return normalizedTextOf(factsheet?.personality) === query;
    case '$':
      return Boolean(factsheet && includesText(factsheet.occupation, query));
    case '&':
      return Boolean(factsheet && includesText(factsheet.referrerName, query));
    default:
      return true;
  }
};

/**
 * V1 Smart Search parity. Symbol-prefixed tokens are ANDed with each other;
 * remaining words use the same factsheet/contact fields as the old
 * react-search-input filter. Every factsheet read is adviser-qualified first.
 */
export const filterClientsBySearch = (
  clients: QueueClient[],
  searchTerm: string,
  adviserEmail?: string
): QueueClient[] => {
  const normalizedTerm = searchTerm.replace(/vvip/gi, 'v.vip').trim();
  if (!normalizedTerm) return clients;

  const tokens = normalizedTerm.split(/\s+/).filter(Boolean);
  const powerTokens = tokens
    .filter(token => POWER_PREFIXES.has(token.charAt(0)))
    .map(token => ({ prefix: token.charAt(0), value: token.slice(1) }));
  const plainTerms = tokens
    .filter(token => !POWER_PREFIXES.has(token.charAt(0)))
    .map(token => token.toLocaleLowerCase());

  return clients.filter(client => {
    const factsheet = adviserFactsheetOf(client, adviserEmail);
    if (!powerTokens.every(token => matchesPowerToken(client, factsheet, token.prefix, token.value))) return false;

    const searchable = searchableValuesOf(client, factsheet).map(normalizedTextOf).filter(Boolean);
    return plainTerms.every(term => searchable.some(value => value.includes(term)));
  });
};

const stringArrayOf = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter(item => typeof item === 'string') : [];

const boundedNumberOf = (value: unknown, fallback: number, minimum: number, maximum: number): number => {
  const parsed = numberOf(value);
  return parsed === null ? fallback : Math.min(maximum, Math.max(minimum, parsed));
};

export type VoiceClientFilterResult = {
  searchTerm: string;
  filters: ClientFilters;
};

/**
 * Converts the existing filter_client voice prompt response into ClientV2's
 * filter model. The prompt deliberately keeps the V1 field names.
 */
export const parseVoiceClientFilter = (message: string): VoiceClientFilterResult | null => {
  try {
    const cleaned = message.replace(/^\s*```json\s*/i, '').replace(/\s*```\s*$/, '');
    const data = JSON.parse(cleaned) as Record<string, unknown>;
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

    const filters = createDefaultClientFilters();
    filters.importance = stringArrayOf(data.importance);
    filters.gender = stringArrayOf(data.gender).map(value => (value ? value : UNSPECIFIED_GENDER));
    filters.personality = stringArrayOf(data.personality).map(value => (value ? value : UNSET_PERSONALITY));
    filters.existingClient = stringArrayOf(data.existingClient);
    filters.source = stringArrayOf(data.source);
    filters.families = stringArrayOf(data.families);
    filters.tags = stringArrayOf(data.tags);
    filters.referrerName = textOf(data.referrerName);
    filters.monthlyIncome = boundedNumberOf(data.monthlyIncome, 0, 0, Number.MAX_SAFE_INTEGER);
    filters.monthlySaving = boundedNumberOf(data.monthlySaving, 0, 0, Number.MAX_SAFE_INTEGER);
    filters.lifeCoverage = boundedNumberOf(data.lifeCoverage, 0, 0, Number.MAX_SAFE_INTEGER);
    filters.ciCoverage = boundedNumberOf(data.ciCoverage, 0, 0, Number.MAX_SAFE_INTEGER);
    filters.ageMin = boundedNumberOf(data.ageMin, 0, 0, 100);
    filters.ageMax = boundedNumberOf(data.ageMax, 100, filters.ageMin, 100);

    const birthdayMonth = boundedNumberOf(data.bdayMonth, 0, 0, 12);
    if (birthdayMonth > 0) {
      filters.birthday = 'month';
      filters.birthdayMonth = birthdayMonth;
    }

    return {
      searchTerm: textOf(data.searchTerm),
      filters
    };
  } catch (error) {
    return null;
  }
};
